import { RedoOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Input,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  Typography,
} from "antd";
import { CvConfigDto } from "api/generated";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";

interface Period {
  label: string;
  value: string;
  ignoreTime?: boolean;
}

interface HumanCronExpressionInputProps {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  showAdvancedModeToggle?: boolean;
  availablePeriods?: Period[];
  config?: CvConfigDto;
}

// ─── Periodos disponibles ─────────────────────────────────────────────────────

export const DAILY_PERIOD: Period = { label: "Diariamente", value: "*/1" };
export const EVERY_2_HOURS_PERIOD: Period = {
  label: "Cada 2 horas",
  value: "*/2",
  ignoreTime: true,
};
export const DEFAULT_PERIODS: Period[] = [DAILY_PERIOD, EVERY_2_HOURS_PERIOD];

// ─── Constantes ───────────────────────────────────────────────────────────────

const TIME_FORMAT = "HH:mm";
const DEFAULT_TIME = "17:00";
const DEFAULT_PERIOD_VALUE = "*/1";

// ─── Validación ───────────────────────────────────────────────────────────────

function isValidCronSegment(segment: string): boolean {
  return /^(\*|\d+|\*\/\d+|\d+-\d+|(\d+,)+\d+)$/.test(segment);
}

function isValidCron(input: string, regexp?: string): boolean {
  if (regexp) {
    // Valdiando con Regrex
    const reg = new RegExp(regexp);
    return reg.test(input);
  }
  const parts = input.trim().split(/\s+/);
  // Cron estándar: 5 partes. Con segundos: 6. Con año: 7.
  if (parts.length < 5 || parts.length > 7) return false;
  return parts.every(isValidCronSegment);
}

function parsePeriodFromCron(
  cron: string | undefined,
  periods: Period[],
): string | null {
  if (!cron) return null;
  const parts = cron.trim().split(/\s+/);
  const periodIgnoringTime = periods.find(
    (p) => p.ignoreTime && parts[2] === p.value,
  );
  if (periodIgnoringTime) return periodIgnoringTime.value;
  const candidate = parts[3];
  const known = periods.find((p) => p.value === candidate);
  return known ? known.value : null;
}

function parseHourFromCron(
  cron: string | undefined,
  periods: Period[],
): string {
  if (!cron) return DEFAULT_TIME;
  const parts = cron.trim().split(/\s+/);
  // Si el cron es de tipo ignoreTime (ej: "0 0 */2 * * *"), parts[2] no es una hora
  const isIgnoreTimeCron = periods.some(
    (p) => p.ignoreTime && parts[2] === p.value,
  );
  if (isIgnoreTimeCron) return DEFAULT_TIME;
  const hora = parts[2]?.padStart(2, "0") ?? "05";
  const minuto = parts[1]?.padStart(2, "0") ?? "00";
  return `${hora}:${minuto}`;
}

function buildCronWithTime(time: string, period: string): string {
  const [hora, minuto] = time.split(":");
  return `0 ${minuto} ${hora} ${period} * *`;
}

function buildCronWithoutTime(period: string): string {
  return `0 0 ${period} * * *`;
}

// ─── Vista de solo lectura ────────────────────────────────────────────────────

function CronReadOnly({
  value,
  availablePeriods,
  isRequired,
}: {
  value: string | undefined;
  availablePeriods: Period[];
  isRequired?: boolean;
}) {
  // Si no hay valor, mostrar "sin valor"
  if (!value) {
    return isRequired ? (
      <Typography.Text type="danger" style={{ fontStyle: "italic" }}>
        (requerido - sin valor)
      </Typography.Text>
    ) : (
      <Typography.Text type="secondary">(sin valor)</Typography.Text>
    );
  }

  const recognizedPeriod = parsePeriodFromCron(value, availablePeriods);
  const periodDef = availablePeriods.find((p) => p.value === recognizedPeriod);

  if (!recognizedPeriod || !periodDef) {
    return <Typography.Text code>{value ?? "—"}</Typography.Text>;
  }

  if (periodDef.ignoreTime) {
    return <Typography.Text>{periodDef.label}</Typography.Text>;
  }

  const time = parseHourFromCron(value, availablePeriods);
  return (
    <Typography.Text>
      {periodDef.label} a las <Typography.Text strong>{time}</Typography.Text>
    </Typography.Text>
  );
}

export function HumanCronExpressionInput({
  value,
  onChange,
  readOnly = false,
  showAdvancedModeToggle = false,
  availablePeriods = DEFAULT_PERIODS,
  config,
}: HumanCronExpressionInputProps) {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [rawError, setRawError] = useState<string | undefined>();

  const defaultValue = config?.defaultValue;

  const recognizedPeriod = parsePeriodFromCron(value, availablePeriods);
  const currentPeriod = recognizedPeriod ?? DEFAULT_PERIOD_VALUE;
  const currentTime = parseHourFromCron(value, availablePeriods);

  const selectedPeriodDef = availablePeriods.find(
    (p) => p.value === currentPeriod,
  );
  const periodIgnoresTime = selectedPeriodDef?.ignoreTime ?? false;

  function emitChange(time: string, period: string) {
    const periodDef = availablePeriods.find((p) => p.value === period);
    const cron = periodDef?.ignoreTime
      ? buildCronWithoutTime(period)
      : buildCronWithTime(time, period);
    onChange?.(cron);
  }

  function handlePeriodChange(period: string) {
    emitChange(currentTime, period);
  }

  function handleTimeChange(time: Dayjs | null) {
    if (!time) return;
    emitChange(time.format(TIME_FORMAT), currentPeriod);
  }

  function handleRawChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setRawError(
      isValidCron(raw, config?.validationRegex)
        ? undefined
        : "Expresión cron inválida. Ejemplo: 0 30 16 */1 * *",
    );
    onChange?.(raw);
  }

  function handleReset() {
    if (!defaultValue) return;
    setRawError(undefined);
    onChange?.(defaultValue);
  }

  const canReset = defaultValue !== undefined && value !== defaultValue;

  // ── Modo lectura ──────────────────────────────────────────────────────────
  if (readOnly) {
    return (
      <CronReadOnly
        value={value}
        availablePeriods={availablePeriods}
        isRequired={config?.isRequired}
      />
    );
  }

  // ── Modo edición ──────────────────────────────────────────────────────────
  const incompatibleWithSimple = recognizedPeriod === null;
  const showSimple =
    !incompatibleWithSimple && showAdvancedModeToggle && !isAdvanced;
  const showRaw =
    incompatibleWithSimple || !showAdvancedModeToggle || isAdvanced;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <Row gutter={8} align="middle" wrap={false}>
        <Col flex="auto">
          {showSimple && (
            <Space.Compact block>
              <Select
                value={currentPeriod}
                options={availablePeriods.map((p) => ({
                  label: p.label,
                  value: p.value,
                }))}
                onChange={handlePeriodChange}
                style={{ width: periodIgnoresTime ? "100%" : "50%" }}
              />
              {!periodIgnoresTime && (
                <TimePicker
                  format={TIME_FORMAT}
                  value={dayjs(currentTime, TIME_FORMAT)}
                  onChange={handleTimeChange}
                  allowClear={false}
                  use12Hours={false}
                  style={{ width: "50%" }}
                />
              )}
            </Space.Compact>
          )}

          {showRaw && (
            <Input
              className="common-input"
              value={value}
              onChange={handleRawChange}
              placeholder="0 30 16 */1 * *"
              status={rawError ? "error" : undefined}
            />
          )}
        </Col>

        {showAdvancedModeToggle && !incompatibleWithSimple && (
          <Col>
            <Switch
              checked={!isAdvanced}
              checkedChildren="Simple"
              unCheckedChildren="Avanzado"
              onChange={(checked) => {
                setIsAdvanced(!checked);
                setRawError(undefined);
              }}
            />
          </Col>
        )}

        {canReset && (
          <Col>
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={handleReset}
            />
          </Col>
        )}
      </Row>

      {showSimple && (
        <Typography.Text type="secondary">
          {periodIgnoresTime
            ? "La rutina se ejecutará automáticamente con la frecuencia seleccionada."
            : "Selecciona con qué frecuencia y a qué hora se ejecutará la rutina (formato 24h)."}
        </Typography.Text>
      )}

      {showRaw && (
        <Typography.Text type={rawError ? "danger" : "secondary"}>
          {rawError ??
            (incompatibleWithSimple
              ? "Esta expresión no es compatible con el modo simple, puedes editarla manualmente."
              : "Ingresa una expresión cron válida. Ejemplo: 0 30 16 */1 * *")}
        </Typography.Text>
      )}

      {canReset && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Valor por defecto:{" "}
          <Typography.Text code style={{ fontSize: 12 }}>
            {defaultValue}
          </Typography.Text>
        </Typography.Text>
      )}
    </div>
  );
}
